import {
  BellRing,
  Compass,
  DatabaseBackup,
  FileCheck,
  FileSpreadsheet,
  FlaskConical,
  Handshake,
  KeyRound,
  Layers,
  Map as MapIcon,
  Rss,
  ScrollText,
  Server,
  Settings2,
  ShieldCheck,
} from 'lucide-react'
import type { DocSection } from './types'

/**
 * The admin handbook (/admin/docs): how Fruuit Engage works today, with "Demo mode" and
 * "In production" callouts. Every statement was checked against the code (apps/api,
 * packages/core, packages/shared, wrangler.jsonc) on 2026-10-22; where the docs and the
 * code disagree, this follows the code. Keep it that way: when behaviour changes, change
 * the matching section here in the same commit.
 */
export const DOC_SECTIONS: readonly DocSection[] = [
  // ---------------------------------------------------------------------------------------
  {
    id: 'overview',
    title: 'Overview',
    summary: 'What Fruuit Engage is, its three areas and who uses each.',
    icon: Compass,
    keywords: ['areas', 'investor', 'workspace', 'admin', 'introduction'],
    blocks: [
      {
        type: 'p',
        text: 'Fruuit Engage is the investor portal for Fruuit Consulting’s stewardship and norms-screening service. Fruuit’s research team assesses companies, runs engagements with them and records the evidence; investor clients (asset owners and managers) see the reviewed, released results for the companies and engagements they are entitled to.',
      },
      {
        type: 'p',
        text: 'It is one web app at `portal.fruuitengage.com` with three areas. Which areas a person sees is decided by the server from their roles (`GET /api/v1/me` returns the list); the browser only hides what the server already refuses.',
      },
      {
        type: 'table',
        head: ['Area', 'Path', 'Who', 'What it is for'],
        rows: [
          [
            'Investor',
            '`/app`',
            'Members of an investor org (`investor`, `investor_admin`)',
            'Portfolio coverage, company dossiers, subscribed engagements, meetings, questions, participation requests, reports and exports. Published research only.',
          ],
          [
            'Workspace',
            '`/workspace`',
            'Fruuit staff (`manager`, `reviewer`)',
            'Research records, the review queue, engagement cases, meetings and minutes, signals, the cross-client inbox. Internal drafts live here.',
          ],
          [
            'Admin',
            '`/admin`',
            'Platform admins and operators',
            'Orgs, users, staff, invitations, subscriptions and dataset entitlements, data feeds, alerts, the audit trail and this handbook.',
          ],
        ],
      },
      {
        type: 'p',
        text: 'A person can hold several roles: everyone admitted as a platform admin also gets `manager` and `reviewer` in the Fruuit org, so they see all three areas through the area switcher in the top bar.',
      },
      {
        type: 'callout',
        mode: 'demo',
        text: 'This deployment is a **demo**. Every company, investor, holding, event and feed record is fictional, and the data feeds generate demo records. The real people who sign in (George and the Fruuit team) are real, so never type real client or company data into it.',
      },
      {
        type: 'callout',
        mode: 'production',
        text: 'The production build is a separate deployment built from the PRD and the agreement: per-tenant databases, staging and production environments, required MFA and a licensed feed. The callouts in each section say exactly what changes.',
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'architecture',
    title: 'Architecture & hosting',
    summary: 'Workers, hostnames, D1, KV, R2 and cron, and the production target.',
    icon: Server,
    keywords: [
      'cloudflare',
      'worker',
      'd1',
      'kv',
      'r2',
      'neon',
      'hyperdrive',
      'hosting',
      'handover',
    ],
    blocks: [
      {
        type: 'p',
        text: 'Everything runs on Cloudflare in a pnpm/turbo monorepo: a Hono API on Workers, a React 19 + Tailwind single-page app served as static assets by the same Worker, and a separate assets-only marketing site.',
      },
      {
        type: 'table',
        head: ['Piece', 'Name', 'Serves'],
        rows: [
          [
            'Portal Worker',
            '`fruuit-engage-portal`',
            '`portal.fruuitengage.com`: the API at `/api/v1/*`, `/healthz`, the SPA from `apps/web/dist` and the cron jobs',
          ],
          [
            'Marketing Worker',
            '`fruuit-engage-marketing`',
            '`fruuitengage.com` and `www.fruuitengage.com` (static pages only)',
          ],
          [
            'D1 database (`DB`)',
            '`fruuit-engage`',
            'All relational data for every org, in Western Europe',
          ],
          [
            'KV namespace (`KV`)',
            '`fruuit-engage-kv`',
            'Sessions (`sess:`), sign-in codes (`otp:`), MFA enrolment (`mfa:`), OAuth state (`oauth:`), rate limits (`rl:`)',
          ],
          [
            'R2 bucket (`FILES`)',
            '`fruuit-engage-files`',
            'Export files (`exports/`, `reports/`), audit anchors (`audit-anchors/`), nightly backups (`backups/`)',
          ],
          [
            'Cron Triggers',
            '`0 * * * *` and `30 2 * * *`',
            'Hourly feed work; nightly audit verification, feed summary, backup and platform health (see [Alerts & scheduled jobs](#jobs))',
          ],
        ],
      },
      {
        type: 'p',
        text: 'Neither Worker has a `workers.dev` address or preview URLs. Every API request passes the same middleware chain: request log → 5xx alerting → security headers → request id → config guard → session → denial audit → CSRF → rate limit → handler. Background work uses Cron Triggers and `waitUntil`; there are no Queues.',
      },
      {
        type: 'callout',
        mode: 'demo',
        items: [
          '**One D1 database** holds every org. Isolation is logical: org-scoped tables carry `org_id`, every read goes through a scoped repository layer and the policy module (see [Data isolation](#isolation)).',
          '**One deployed environment** ("production-demo") plus local dev. No staging.',
          '**One SPA and one session realm** on one hostname, with three role-gated areas. The investor’s org comes from their membership, never from the hostname.',
          'Hosted in the **Consultant-controlled Cloudflare account** (George’s), which stays that way until the Handover Date (agreement §7A).',
        ],
      },
      {
        type: 'callout',
        mode: 'production',
        items: [
          '**Tenant-separated data planes** (PRD D-02): D1 + KV as the control plane, a Fruuit core database on Neon Postgres, and **one Neon project per investor tenant**, reached through Hyperdrive bindings `HD_T_<slug>` generated at deploy time. Database clients are per request and never cached.',
          '**Separate apps per realm** (PRD D-09): investor portal on `{tenant}.<portal-domain>`, `workspace.` and `admin.` hostnames, plus the API and a CLI. Background work uses Queues as well as cron.',
          '**Staging and production** environments, each with its own D1/KV, Neon, Hyperdrive, R2, Queues, secrets and OAuth apps (P-OPS-001).',
          'Both run in Consultant-controlled Cloudflare and Neon accounts until the **Handover Date** (agreement §7A), when the source, keys and accounts transfer to Fruuit and the Neon projects move to Fruuit’s org.',
          'Before any real client data: decide EU-only storage (PRD Q11). D1 is in Western Europe today, but R2 and KV use default locations; recreate them with the `eu` jurisdiction if required.',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'identity',
    title: 'Sign-in & access',
    summary: 'Google, Microsoft and email codes; who is admitted; sessions, CSRF, invites and MFA.',
    icon: KeyRound,
    keywords: [
      'login',
      'oauth',
      'otp',
      'session',
      'csrf',
      'invite',
      'mfa',
      'totp',
      'admission',
      'entra',
      'PRIVILEGED_MFA',
    ],
    blocks: [
      { type: 'h', text: 'Sign-in methods' },
      {
        type: 'list',
        items: [
          '**Google** and **Microsoft** (OpenID Connect, authorization code with PKCE, state and nonce). Each button appears only when both its client id and secret are set. Microsoft uses the `organizations` endpoint, so only work or school accounts; personal Microsoft accounts are refused.',
          '**Email code**: a 6-digit code, valid 15 minutes, single use, 60 seconds between sends. Five wrong attempts lock the address for 30 minutes. It needs `RESEND_API_KEY` (or the local-only dev log). Unknown addresses get a silent decoy: the screen looks the same, nothing is sent and no code will ever work, so the form never reveals who has access.',
          'Public sign-in endpoints are rate-limited (30 per minute per IP); every other write is limited to 120 per minute per user.',
        ],
      },
      { type: 'h', text: 'Who is admitted' },
      {
        type: 'p',
        text: 'The portal is **invite-only**, with one exception: the admission rule, evaluated at every sign-in.',
      },
      {
        type: 'list',
        items: [
          'A verified email on `@fruuitconsulting.com` (`ADMIN_DOMAIN`), or listed in `ADMIN_EXTRA_EMAILS` or `OPERATOR_EMAILS` (today `george@plsft.com` and `george@georgerios.com`), becomes a **platform admin**. Addresses in `OPERATOR_EMAILS` become **platform operators**.',
          'The first time someone is admitted this way they also get `manager` and `reviewer` in the Fruuit org. Roles an admin later removes stay removed.',
          '**Microsoft tenant rule:** a Microsoft sign-in for an `@fruuitconsulting.com` address must come from Fruuit’s own Entra tenant (`FRUUIT_ENTRA_TID`), or the sign-in is refused outright. Google and email codes have no tenant check.',
          'The rule is re-checked at every sign-in: someone the configuration no longer admits loses the platform role.',
          'Everyone else must already have an account or a **pending invitation for that exact email**. A matching invite is accepted automatically when they sign in. Anyone else sees a neutral “no access” page; every refusal is audited as `auth.denied`.',
        ],
      },
      { type: 'live', view: 'auth' },
      { type: 'h', text: 'Sessions' },
      {
        type: 'list',
        items: [
          'An opaque 256-bit token in the `fe_session` cookie (HttpOnly, Secure, SameSite=Lax). Only its SHA-256 is stored, in KV.',
          '**12-hour idle timeout**, sliding with use, and a **30-day absolute cap**. Signing in again replaces any session the browser held.',
          'The user, memberships and org status are re-read from the database on **every request**. A deactivated user is signed out on their next request.',
          '**Sign out** ends this session. **Sign out everywhere** ends all of the person’s sessions. People can see and end individual sessions under Account; admins can revoke all of a user’s sessions from Users.',
        ],
      },
      { type: 'h', text: 'CSRF and headers' },
      {
        type: 'p',
        text: 'Every non-GET request must come from the portal’s own origin (checked against `Origin`) and carry an `X-CSRF-Token` header that matches the `fe_csrf` cookie and an HMAC of the session id. Responses carry HSTS, a strict Content-Security-Policy, `X-Frame-Options: DENY` and `Cache-Control: no-store`.',
      },
      { type: 'h', text: 'Invitations' },
      {
        type: 'list',
        items: [
          'Platform admins invite to any org; an `investor_admin` invites to their own org only. Managers cannot invite.',
          'An invitation is valid for **7 days** and is **single use**. Only a hash of its token is stored. Resend issues a new link and restarts the 7 days; revoke cancels it.',
          'If a platform admin invites someone who already has an active account, the role is granted at once and no email is sent.',
        ],
      },
      { type: 'h', text: 'Second factor (MFA)' },
      {
        type: 'p',
        text: 'Authenticator-app codes (TOTP) with 10 single-use recovery codes are built in (Account → Security). When `PRIVILEGED_MFA=required`, a session must have passed a second factor before any privileged action: approving, publishing, returning or retracting records, reviewing minutes, managing subscriptions, entitlements, orgs, users, staff, operator grants, invitations, running feeds, and “View as”. A Google or Microsoft sign-in that already used MFA counts.',
      },
      {
        type: 'callout',
        mode: 'demo',
        items: [
          '`PRIVILEGED_MFA=optional`: people can enrol, but nothing requires it (ADR-D03).',
          '**Microsoft for outside organisations needs admin consent.** The Entra app’s publisher is not verified yet, so a customer tenant’s administrator must grant consent once (Fruuit’s own tenant too, before staff can use “Continue with Microsoft”). Email codes and Google work meanwhile.',
          'The Google OAuth app is published with no logo and George’s Gmail as the support address.',
        ],
      },
      {
        type: 'callout',
        mode: 'production',
        items: [
          'Set `PRIVILEGED_MFA=required` before any real client data. The marketing site’s security page already promises MFA for staff.',
          'Verify the Microsoft publisher (MPN ID) so customers can consent without an admin, and register separate OAuth apps for staging and production.',
          '**Rotate the Microsoft client secret before it expires on 2027-10-22.**',
          'The PRD describes a separate admin realm with its own cookie (D-19); the demo uses one session realm for all three areas.',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'roles',
    title: 'Roles & permissions',
    summary: 'The six roles, what each can do, 404 versus 403, and “View as”.',
    icon: ShieldCheck,
    keywords: [
      'platform_operator',
      'platform_admin',
      'manager',
      'reviewer',
      'investor',
      'investor_admin',
      'impersonation',
      'view as',
      'policy',
      '403',
      '404',
    ],
    blocks: [
      {
        type: 'p',
        text: 'Every permission check goes through one function, `can()` in `packages/core`, with the action names listed in `packages/shared/src/actions.ts`. There are six roles, held in three places:',
      },
      {
        type: 'table',
        head: ['Role', 'Held as', 'In short'],
        rows: [
          [
            '`platform_operator`',
            'Platform role',
            'Everything a platform admin can do, plus “View as”, granting operators and re-running feeds by hand.',
          ],
          [
            '`platform_admin`',
            'Platform role',
            'The admin area: orgs, users, staff, invitations, subscriptions, entitlements, alerts.',
          ],
          [
            '`manager`',
            'Fruuit org membership',
            'Researches and runs cases: drafts records, manages meetings and minutes as the assigned manager, answers questions.',
          ],
          [
            '`reviewer`',
            'Fruuit org membership',
            'Independent review: approves, publishes, returns, retracts and releases work they did not contribute to.',
          ],
          [
            '`investor_admin`',
            'Investor org membership',
            'An investor who can also invite colleagues and read everyone’s questions and requests in their org.',
          ],
          [
            '`investor`',
            'Investor org membership',
            'Reads released content in scope; asks questions, requests participation, records client actions.',
          ],
        ],
      },
      {
        type: 'p',
        text: '“Contributor” is not a role: it is anyone who created or edited a particular draft, recorded per record.',
      },
      {
        type: 'table',
        caption: 'Who can do what',
        head: ['Capability', 'Allowed for'],
        rows: [
          ['Admin area: orgs, users, staff, invitations to any org', 'Platform admin, operator'],
          [
            'Subscriptions, per-user restrictions, dataset entitlements',
            'Platform admin, operator (managers cannot)',
          ],
          ['Acknowledge alerts', 'Platform admin, operator'],
          ['Grant or revoke operators; “View as”; manual feed re-run', 'Operator only'],
          ['Read all research, cases, meetings and minutes', 'Manager, reviewer'],
          ['Create and edit drafts (records, engagements, objectives)', 'Manager, reviewer'],
          ['Submit a draft for review', 'Staff who contributed to that draft'],
          [
            'Approve, publish, return, retract; release engagements and objectives',
            'Reviewer who did not contribute. Milestones, correspondence and minutes: only the case’s assigned reviewer',
          ],
          [
            'Create, publish, cancel, reschedule meetings; write minutes',
            'The case’s assigned manager',
          ],
          ['Act on participation requests', 'Assigned manager (any manager may reassign)'],
          ['Reply to investor questions', 'Any manager (or the asker)'],
          ['Triage signals (dismiss, promote)', 'Manager, reviewer'],
          ['Resolve mapping exceptions', 'Manager'],
          ['Edit or import holdings', 'Manager, or the org’s investor_admin'],
          ['Invite colleagues to their own org', 'investor_admin'],
          [
            'Read released content in scope; ask questions; request participation; record client actions',
            'investor, investor_admin',
          ],
          ['Reports and CSV exports', 'Org members (their org), staff (any org)'],
          [
            'Audit trail',
            'Platform chain: platform admins. Core chain: staff and platform admins. Org chains: staff only (they carry client content)',
          ],
        ],
      },
      { type: 'h', text: '404 versus 403' },
      {
        type: 'list',
        items: [
          '**404 Not found** when you cannot even see the thing: another org’s records, an engagement you are not subscribed to, someone else’s private request. The answer is identical to “does not exist”, so the API is never an existence oracle.',
          '**403 Forbidden** when you can see it but may not do this: a manager trying to approve, a reviewer approving their own work (with the reason “You contributed to this draft…”), a non-admin opening an admin endpoint.',
          'Special 403s: `mfa_required` (second factor needed) and `impersonation_restricted` (not allowed while viewing as someone). 401 means not signed in or session expired.',
        ],
      },
      { type: 'h', text: '“View as” (impersonation)' },
      {
        type: 'list',
        items: [
          '**Operators only**, from Admin → Users, with a reason of at least 8 characters. Targets must be active, non-platform users with an active membership (investors or staff).',
          'Starts a separate **60-minute** session that never extends. The operator’s own session is kept and restored on exit.',
          'An amber banner shows whose view it is, who started it and the minutes left, with an **Exit impersonation** button.',
          'Audited: `impersonation.start` and `impersonation.end` on the platform chain, and every action taken carries `impersonated_by`.',
          '**Blocked while impersonating:** MFA and sign-in identities, invitations, starting another impersonation, operator changes, sign out everywhere, ending sessions and accepting invitations. Everything else the user could do is allowed, and audited as such.',
        ],
      },
      {
        type: 'callout',
        mode: 'demo',
        text: 'Seeded persona accounts have no sign-in identity, so nobody can sign in as them; use “View as” to see the portal through an investor’s eyes.',
      },
      {
        type: 'callout',
        mode: 'production',
        items: [
          'The PRD (P-ID-016) says operators read client data **only** through audited impersonation. In this build an admitted operator also holds manager and reviewer, which gives full staff read access. Remove those roles from operators (Admin → Staff) or change the admission rule before real data.',
          'With `PRIVILEGED_MFA=required`, starting “View as” needs a second factor.',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'isolation',
    title: 'Data isolation & subscriptions',
    summary: 'What an investor can see, and how subscriptions, entitlements and restrictions work.',
    icon: Layers,
    keywords: [
      'tenant',
      'org',
      'subscription',
      'entitlement',
      'restriction',
      'authorized companies',
      'revocation',
      'scope',
    ],
    blocks: [
      {
        type: 'p',
        text: 'Every org-scoped query runs inside a **scope** built from the caller’s memberships, never from anything in the request. An investor who passes another org’s id simply gets 404. Staff read across orgs through a handful of paths. The cross-client inbox and the staff views of questions, participation requests, client actions and cases log each read as `inbox.read`; the staff dashboard, the client picker and the export history are cross-org aggregates that are permission-checked but not logged per read.',
      },
      { type: 'h', text: 'What an investor sees' },
      {
        type: 'list',
        items: [
          '**Engagements** that are released (not archived, not retracted) and either specific to their own org or shared and **subscribed** by their org, unless a per-user restriction hides it from them.',
          '**Objectives** and engagement summaries as last **released**, never the working copy. **Milestones** and **correspondence** only when published.',
          '**Assessments** only when published and only for **authorized companies**.',
          '**Sources** only when marked shareable with clients and redistributable; **incidents** only when cited by a published assessment they can see. Hidden references are counted (“2 further evidence references are internal to Fruuit”), never listed.',
          'Investor responses use an allowlist of fields: no internal user ids, review notes or draft states; staff appear by display name only.',
        ],
      },
      {
        type: 'p',
        text: '**Authorized companies** are, in the main path (company dossiers and coverage), the union of: companies in the org’s holdings with a verified mapping, companies on the org’s coverage list, and the companies of its subscribed engagements.',
      },
      { type: 'h', text: 'Subscriptions, entitlements, restrictions' },
      {
        type: 'table',
        head: ['Control', 'What it grants or hides', 'Managed by'],
        rows: [
          [
            'Engagement subscription',
            'A shared engagement becomes visible to an investor org (a client-specific case is always visible to its own client).',
            'Platform admins, with a reason, in [Subscriptions](/admin/subscriptions)',
          ],
          [
            'Dataset entitlement',
            'Feed-derived identifiers and research for one dataset (`fruuit-screening.signals`, `news-monitor.articles`, `company-disclosures.filings`) in the company views.',
            'Platform admins, with a reason; one active grant per org and dataset',
          ],
          [
            'Per-user restriction',
            'Hides one engagement (and its objectives, milestones, correspondence, meetings and minutes) from one investor user while the org stays subscribed.',
            'Platform admins',
          ],
        ],
      },
      {
        type: 'list',
        items: [
          'Every grant, revoke and restriction is audited on the platform chain and on the org’s own chain. Revoked rows are kept as history.',
          '**Revocation takes effect on the next request**: visibility is evaluated per request from the database, and the API never caches authorization (`no-store`). A screen already open may show old data until it refreshes; the server will not serve it again.',
          'Revoking a subscription tells the admin how many objectives, milestones, correspondence items and meetings disappear for that org.',
        ],
      },
      {
        type: 'callout',
        mode: 'demo',
        text: '**Logical isolation in one D1 database** (ADR-D02): every org shares the same tables, separated by `org_id`, the scoped repository layer and the policy module. The repository seam is kept so Neon can replace D1. The isolation tests run on every build.',
      },
      {
        type: 'callout',
        mode: 'production',
        items: [
          '**Physical isolation:** one Neon project per investor tenant holds that client’s private data and its published projections, on top of the same logical checks.',
          '**Publication fan-out (D-17):** when content is released or a subscription or entitlement changes, an outbox row in the core database queues per-tenant jobs that copy the released versions into each subscribing tenant’s database, and remove them on revocation. Investor requests only ever touch their own tenant database. Jobs are idempotent, retried, with a dead-letter queue, and the admin panel shows projection lag and re-sync.',
          'Hyperdrive query caching stays **off** (P-TEN-006): authorization-sensitive reads are never served stale.',
          'The PRD lets an investor admin narrow access per user (D-04); in this build only platform admins manage restrictions.',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'workflow',
    title: 'Research & review workflow',
    summary: 'Draft to published, independent review, release, versions and corrections.',
    icon: FileCheck,
    keywords: [
      'draft',
      'in review',
      'approved',
      'published',
      'returned',
      'retracted',
      'release',
      'correction',
      'version',
      'immutable',
      'revision',
      'internal',
    ],
    blocks: [
      {
        type: 'p',
        text: 'Assessments, milestones and correspondence (and minutes, through their own screens) follow one review state machine. Engagements and objectives use **release** instead (below).',
      },
      {
        type: 'table',
        head: ['Step', 'From', 'To', 'Who', 'Reason'],
        rows: [
          [
            'Submit',
            'draft, returned',
            'in review',
            'A staff member who worked on the draft',
            'Optional note',
          ],
          ['Approve', 'in review', 'approved', 'An independent reviewer', 'Required'],
          ['Publish', 'approved', 'published', 'An independent reviewer', 'Required'],
          ['Return', 'in review, approved', 'returned', 'An independent reviewer', 'Required'],
          ['Retract', 'published', 'retracted', 'An independent reviewer', 'Required'],
        ],
      },
      {
        type: 'list',
        items: [
          'Reasons are 8 to 12,000 characters. Returning clears the approval.',
          '**Reviewer independence:** anyone who created or edited the draft cannot approve, publish, return or retract it; another reviewer must. For milestones, correspondence and minutes the reviewer must also be the case’s assigned reviewer. Minutes can only be published by the reviewer who approved them.',
          'A “No material concern identified” assessment cannot be published without a completed search run.',
          'Records can be edited only in draft or returned. Investors see only published records.',
        ],
      },
      { type: 'h', text: 'Releasing engagements and objectives' },
      {
        type: 'p',
        text: 'Staff edit an engagement’s or objective’s **working fields** freely. Nothing reaches investors until a reviewer who has not edited it since the last release **releases** it, with a reason. Release copies the working fields into the released copy that investors read: for engagements the title, theme, status, next action and due date; for objectives the title, baseline, change requested, next action and due date. Releasing with no unreleased changes is refused.',
      },
      { type: 'h', text: 'Versions, corrections and immutability' },
      {
        type: 'list',
        items: [
          'Published and retracted records are **immutable**: the API answers 409, and database triggers refuse the update as well. Review events and the audit trail are append-only.',
          'To change a published record, **Correct** it: a new draft version is created, linked to the previous one, with a reason, and goes through review again. Only one successor per version.',
          '**History** shows the version chain and every review event with who acted and why.',
          'Every edit sends the record’s `revision`. If someone else changed it first, the save is refused with 409 “This record changed. Refresh and use the latest version.” and nothing is written.',
        ],
      },
      {
        type: 'p',
        text: 'In the workspace, items investors cannot see carry an amber, dashed **“Internal – not visible to investors”** tag (released items show “Released to investors”). The case summary is always internal because it shows working fields; the “What investors see” card shows the released copy.',
      },
      {
        type: 'callout',
        mode: 'note',
        text: 'Engagements and objectives have no retract action in this build; to withdraw one from investors, revoke the subscription or archive the case.',
      },
      {
        type: 'callout',
        mode: 'demo',
        text: 'The seeded world has published records, versions still in review (visible in the workspace, not to investors) and deliberate corrections (the Kestrelmoor and Corriebrae assessments v3, the Kestrelmoor March minutes v2), so the workflow can be shown end to end. Kestrelmoor’s unreleased S3 milestone is the example of internal work investors cannot see.',
      },
      {
        type: 'callout',
        mode: 'production',
        text: 'Same workflow. Released versions are additionally copied into each subscribing tenant’s database by the publication fan-out, and a retraction or revocation removes them through the same path.',
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'engagements',
    title: 'Engagements, meetings & minutes',
    summary:
      'Objectives and stages, milestones, responsiveness, meetings, minutes and participation.',
    icon: Handshake,
    keywords: [
      'objective',
      'stage',
      'S0',
      'S4',
      'milestone',
      'evidence',
      'responsiveness',
      'meeting',
      'minutes',
      'participation',
      'ADR-D01',
      'ADR-D04',
      'questions',
      'client actions',
    ],
    blocks: [
      { type: 'h', text: 'Objectives and the stage ladder' },
      {
        type: 'p',
        text: 'Each engagement has objectives. An objective’s stage is the stage of its latest **published** milestone (by assessment date); with none it is S0.',
      },
      {
        type: 'kv',
        head: ['Stage', 'Meaning'],
        rows: [
          ['S0', 'Baseline'],
          ['S1', 'Dialogue established'],
          ['S2', 'Commitment recorded'],
          ['S3', 'Implementation evidenced'],
          ['S4', 'Outcome evidenced'],
        ],
      },
      {
        type: 'list',
        items: [
          '**Milestones** cite evidence (sources). Investors see only evidence from sources shareable with clients, plus a count of the rest.',
          '**What changed** is the milestone’s change reason, or else derived: “First reviewed milestone for this objective.”, “Stage unchanged at S2; new evidence reviewed.” or “Stage moved from S1 to S2.”',
          '**Still outstanding** is the milestone’s limitations field.',
        ],
      },
      { type: 'h', text: 'Company responsiveness' },
      {
        type: 'p',
        text: 'Computed from **published** correspondence only. Follow-up requests are outbound; written responses, company meeting records and evidence received are responses. A follow-up is unanswered unless a response is dated on or after it; days awaiting count from the oldest unanswered follow-up.',
      },
      {
        type: 'kv',
        head: ['Label', 'When'],
        rows: [
          ['Not assessed', 'No follow-ups and no responses at all'],
          ['Unresponsive', '3 or more unanswered follow-ups, or waiting more than 60 days'],
          ['Slow', 'Waiting more than 21 days'],
          ['Responsive', 'Otherwise'],
        ],
      },
      { type: 'h', text: 'Meetings' },
      {
        type: 'list',
        items: [
          'Draft → **published** → (cancelled or superseded). Only drafts can be edited. Publishing needs the case to be released.',
          '**Cancel** (draft or published) and **reschedule** (published) need a reason. Rescheduling creates a new published meeting that supersedes the old one and keeps its objectives.',
          'Closed cases refuse edits, publishing and rescheduling; cancelling still works.',
          'Investors see published meetings in their calendar; the case page also lists cancelled ones.',
        ],
      },
      { type: 'h', text: 'Minutes' },
      {
        type: 'p',
        text: 'The assigned manager writes minutes for a published meeting and submits them; the assigned reviewer (not a contributor) approves, returns or publishes them, and only the approver can publish. Investors see published minutes while the meeting is published and the case released. Corrections create a new version; only one unpublished version may exist at a time.',
      },
      { type: 'h', text: 'Who can read and write (ADR-D01, ADR-D04)' },
      {
        type: 'list',
        items: [
          '**ADR-D01:** all Fruuit staff can read every case’s meetings and minutes; only the case team writes them (the assigned manager drafts, publishes, cancels, reschedules and writes minutes; the assigned reviewer reviews minutes). A non-team manager gets 403. Draft meetings appear on the case page only to the case team, but in the calendar to all staff.',
          '**ADR-D04:** a milestone counts as one of a meeting’s commitments if it is at stage S2–S4, belongs to the meeting’s objectives (or any of the case’s objectives when none are linked), and is dated from 7 days before the meeting up to, but not including, the case’s next published meeting.',
        ],
      },
      { type: 'h', text: 'Participation requests' },
      {
        type: 'table',
        head: ['Action', 'From', 'To', 'By'],
        rows: [
          ['Request', '—', 'requested', 'An investor who can see the case'],
          ['Acknowledge', 'requested, acknowledged', 'acknowledged', 'Assigned manager'],
          [
            'Company consent pending',
            'requested, acknowledged',
            'company consent pending',
            'Assigned manager',
          ],
          ['Confirm', 'acknowledged, company consent pending', 'confirmed', 'Assigned manager'],
          ['Decline', 'any open state', 'declined', 'Assigned manager'],
          ['Withdraw', 'any open state', 'withdrawn', 'The requester'],
          ['Reassign', 'any open state', '(unchanged)', 'Any manager'],
        ],
      },
      {
        type: 'p',
        text: 'Manager responses need a message of 8 to 2,000 characters, an open case, a meeting that has not started, and an org that still has access (otherwise 409). Requesting the same thing twice returns the existing request. Investors can also **ask questions** about a case (answered by any manager) and record **client actions**, their own decisions about a company, which are recorded only, never executed.',
      },
      {
        type: 'callout',
        mode: 'demo',
        text: '**Nothing is ever sent to companies or investors.** There are no email notifications about content and no calendar invitations; meetings, participation and questions live only in the portal. The only emails are sign-in codes, invitations and ops alerts.',
      },
      {
        type: 'callout',
        mode: 'production',
        text: 'The same: outbound email and calendar invitations to companies or investors are deliberately not in v1 of the production scope either. Adding them would be a change order.',
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'reports',
    title: 'Reports & exports',
    summary: 'Previews, CSV exports, the committee report, formula escaping and the export log.',
    icon: FileSpreadsheet,
    keywords: ['csv', 'pdf', 'committee', 'export log', 'sha-256', 'limit', 'xlsx', 'watermark'],
    blocks: [
      {
        type: 'list',
        items: [
          '**Catalogue:** the committee report, the coverage register, engagement cases, published updates, and one CSV per record kind (staff get a few more kinds and columns). Staff must choose a client for client-private reports.',
          '**Preview equals export:** the preview and the CSV run the same query with the same filters; the preview shows the first rows (100 by default) and the total.',
          '**Committee report:** server-rendered HTML with a “Print or save as PDF” button; you keep a PDF through the browser’s print dialog. The period defaults to the last 90 days.',
          "**CSV:** UTF-8 with BOM, CRLF, every field quoted. Any cell whose first non-space character is `=`, `+`, `-`, `@`, a tab or a carriage return (or their full-width forms) is prefixed with `'` so spreadsheets never run it as a formula. Every CSV adds `exported_at` and `prepared_for` (“<name> · Fictional demo data”) columns.",
          '**Limit:** an export of more than **5,000 rows** is refused with 422 and a hint to narrow it by portfolio, period or status. Exports are also limited to 20 per minute per person.',
          '**Export log:** every file (CSV, committee HTML, single-record JSON) is stored in R2 with the **SHA-256** of the exact bytes served, logged with who, when, scope and row count, and audited (`export.csv`, `export.html`, `export.record`) on the org’s chain. The hash is also returned in an `X-Export-Sha256` header. Investors see their org’s log; staff see every org’s.',
        ],
      },
      {
        type: 'callout',
        mode: 'demo',
        text: 'Every export is watermarked as fictional demo data. Stored export files are not deleted automatically yet.',
      },
      {
        type: 'callout',
        mode: 'production',
        items: [
          'XLSX files and a branded PDF are not in scope (PRD D-11); they would be a change order. Print-to-PDF stays the PDF route.',
          'The PRD keeps ad-hoc exports for 90 days; a clean-up job for `exports/` and `reports/` must be added.',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'feeds',
    title: 'Data feeds & signals',
    summary: 'The feed pipeline, the providers, failures and retries, and signal triage.',
    icon: Rss,
    keywords: [
      'fruuit-screening',
      'news monitor',
      'company disclosures',
      'adapter',
      'pipeline',
      'signal',
      'promote',
      'dismiss',
      'lock',
      'retry',
      'late',
    ],
    blocks: [
      {
        type: 'p',
        text: 'Each provider is fetched by a feed adapter through one pipeline: lock → fetch → validate → de-duplicate → map identifiers → store → signals → run record → alerts.',
      },
      {
        type: 'p',
        text: 'The **Fruuit Screening Engine** (`fruuit-screening`, dataset `fruuit-screening.signals`) is Fruuit’s own AI-assisted screening process. It runs outside the portal: its inputs are raw company and controversy data from FactSet, and its results are cross-validated against Sustainalytics research (Fruuit holds both licences). The portal receives only the engine’s output, as screening signals keyed by the engine’s company id, and a human assesses each one before anything is released. **News Monitor** and **Company Disclosures** are the two other feeds.',
      },
      {
        type: 'list',
        ordered: true,
        items: [
          '**Lock:** one run per dataset at a time, through a lock row in D1 with a 10-minute expiry. A run that finds it held is skipped.',
          '**Fetch:** HTTPS to the provider’s single allowed origin only, no redirects, 5-second deadline, 2 MiB cap.',
          '**Validate** against a field allowlist and drop records already stored (by provider id and content hash).',
          '**Map** typed identifiers only (LEI, the provider’s own id, or the ISIN of a verified security). Names are never used; unmapped or ambiguous records go to mapping review.',
          '**Store** a new source version and create a **signal** for each fresh record.',
          '**Record** the run (ok, partial, late, empty or failed) and raise alerts.',
        ],
      },
      { type: 'live', view: 'feeds' },
      {
        type: 'list',
        items: [
          '**Schedule:** the hourly cron runs every due slot, catching up on missed ones. News Monitor’s :15 slot and Company Disclosures’ 06:30 slot are picked up on the next hourly tick.',
          '**Late:** a delivery after its slot plus the provider’s late threshold is marked late; a slot with nothing delivered by then is flagged by the hourly late check.',
          '**Failed:** network errors, rate limits, HTTP errors, invalid or oversized responses and time-outs fail the run. It is retried up to **3 attempts**, 20 then 40 minutes apart, on the next hourly ticks. Attempt 1 failing is a warning, later attempts are critical.',
          '**Manual re-run:** operators can re-run a provider (any slot from the last 7 days) from [Data feeds](/admin/feeds). It is refused if a run started in the last 60 seconds or the lock is held, and it is audited.',
        ],
      },
      { type: 'h', text: 'Signals are candidates, never published' },
      {
        type: 'list',
        items: [
          'A signal starts as **new** (or **mapping review** if its company was not identified). Severity comes from its score: 85+ severe, 65+ serious, 40+ moderate, else low.',
          'Staff **dismiss** it with a reason, or **promote** it, which creates a **draft incident** with an internal source (“Not yet established”, “Unverified feed signal”). The incident then follows the normal review workflow. Nothing from a feed reaches investors automatically.',
          'A signal scoring 70 or more on a company any investor holds raises a “high-severity signal” alert.',
        ],
      },
      {
        type: 'callout',
        mode: 'demo',
        items: [
          'All three providers are **demo feeds**: in-process fixture origins generate deliveries from the fictional world with a fixed random seed, so runs are deterministic and reproducible. About 88% of slots deliver normally; the rest are partial, failed, late or empty on purpose, so the failure handling is visible.',
          'The Fruuit Screening Engine feed is Fruuit’s own AI-assisted screening process, which runs outside the portal: FactSet raw company and controversy data, cross-validated against Sustainalytics research. Fruuit holds both licences. In the demo its deliveries are fictional records generated by the fixture origin; every record carries only a generic 0–100 score, and no FactSet or Sustainalytics data is stored or redistributed.',
        ],
      },
      {
        type: 'callout',
        mode: 'production',
        items: [
          'The real provider adapter goes live on **Provider Access** (the licence, documentation and credentials Fruuit obtains). It is the separate **Feed Milestone** under agreement §6 (“M4 Feed live”) and does not block Portal acceptance, which uses the fixture provider.',
          'The live engine connection replaces the fixture: the portal ingests the engine’s output as signals through the same adapter (typed identifiers, field allowlist, scores 0–100); the AI, FactSet and Sustainalytics steps stay inside Fruuit’s screening process, never inside the portal. Signals remain candidates that a human assesses before anything is released.',
          'Before live data: a licence that covers redistribution to investor clients, the provider’s endpoint and credentials as secrets, and dataset entitlements granted per client to match the licence.',
          'The deterministic fixture provider keeps running the full pipeline in CI and staging (P-FEED-004).',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'jobs',
    title: 'Alerts & scheduled jobs',
    summary: 'Every cron job, the alert kinds and the ops alert email.',
    icon: BellRing,
    keywords: ['cron', 'job', 'alert', 'OPS_ALERT_EMAIL', 'dedupe', 'job_runs', 'email'],
    blocks: [
      {
        type: 'table',
        head: ['Schedule (UTC)', 'Job', 'What it does'],
        rows: [
          [
            '`0 * * * *` hourly',
            '`feed-ingest`',
            'Runs due retries, then every unhandled slot for each provider',
          ],
          ['`0 * * * *` hourly', '`feed-late-check`', 'Flags overdue slots as late'],
          [
            '`30 2 * * *` 02:30',
            '`audit-verify`',
            'Verifies every audit chain and writes the nightly anchor to R2',
          ],
          [
            '`30 2 * * *` 02:30',
            '`feed-health-summary`',
            'Records each provider’s last-24-hour run counts as an info alert',
          ],
          [
            '`30 2 * * *` 02:30',
            '`d1-backup`',
            'Exports every table to R2 and deletes backups older than 35 days',
          ],
          [
            '`30 2 * * *` 02:30',
            '`retention`',
            'Deletes export and report files from R2 after 90 days (the export log keeps the row, stamped `artifact_deleted_at`) and idempotency receipts after 30 days',
          ],
          [
            '`30 2 * * *` 02:30',
            '`platform-health`',
            'Records the last 24 hours at a glance (5xx alerts, failed jobs, open critical alerts, tonight’s audit check and backup) as one alert',
          ],
        ],
      },
      {
        type: 'p',
        text: 'Jobs run one after another and never stop each other. Each run is recorded (see Overview → Scheduled jobs); a job that throws writes a structured `job_failed` error log line, raises a critical `job_failed` alert and is emailed (below).',
      },
      { type: 'live', view: 'cron' },
      {
        type: 'table',
        caption: 'Alert kinds',
        head: ['Kind', 'Severity', 'Raised when'],
        rows: [
          ['`feed_failed`', 'Warning; critical from attempt 2', 'A feed run failed'],
          ['`feed_late`', 'Warning', 'A delivery was late or a slot is overdue'],
          ['`feed_partial`', 'Info', 'A delivery hit the provider’s record cap'],
          [
            '`high_severity_signal`',
            'Critical at 85+, else warning',
            'A signal scoring 70+ on a company an investor holds',
          ],
          ['`feed_health`', 'Info', 'Nightly per-provider summary'],
          ['`audit_verify_failed`', 'Critical', 'A chain failed nightly verification'],
          ['`job_failed`', 'Critical', 'A scheduled job threw an error'],
          [
            '`api_error`',
            'Critical for 503, else warning',
            'The API answered 5xx; one alert per status and route while open',
          ],
          [
            '`platform_health`',
            'Info; warning if anything needs a look',
            'Nightly platform summary',
          ],
        ],
      },
      {
        type: 'list',
        items: [
          'An open alert is not raised twice: a second one with the same key is ignored until the first is acknowledged. Platform admins acknowledge alerts in [Alerts](/admin/alerts); it is audited.',
          '**Email** goes to `OPS_ALERT_EMAIL` through Resend, with the subject prefix “[Fruuit Engage ops]”, for `feed_failed` and `feed_late` (**at most one email per provider per 24 hours**), `job_failed` (at most one per job per 24 hours) and `audit_verify_failed` (one summary per run naming the failing chains, each chain at most once per 24 hours). A failure that is still open a day later is emailed again as a reminder. Everything else is in-app only. Without `RESEND_API_KEY` nothing is emailed.',
          'What to do for each alert is in `docs/RUNBOOK.md` (feed incidents, audit-chain failure, incident checklist).',
        ],
      },
      { type: 'live', view: 'alerting' },
      {
        type: 'callout',
        mode: 'demo',
        text: 'Ops alerts go to George. The demo feeds fail on purpose now and then, so expect an occasional email.',
      },
      {
        type: 'callout',
        mode: 'production',
        items: [
          'Point `OPS_ALERT_EMAIL` at the team that will be on call (a shared mailbox), per environment.',
          'Cron, audit-verification and 5xx alerting follow P-OPS-005 already. The PRD names Cloudflare Email Service; this build uses Resend. A 5xx **rate** threshold (rather than one alert per route) would come from Workers Observability alerting.',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'audit',
    title: 'Audit trail',
    summary: 'Hash-chained audit events, what is logged, verification and nightly anchors.',
    icon: ScrollText,
    keywords: ['hash chain', 'sha-256', 'verify', 'anchor', 'platform', 'core', 'org'],
    blocks: [
      {
        type: 'list',
        items: [
          '**Chains:** `platform` (sign-ins, admin, impersonation, feeds, alerts), `core` (Fruuit research and review) and one `org:<id>` chain per investor org (that org’s activity, plus grants and client-specific content).',
          '**Hashing:** each event’s hash is SHA-256 of the previous event’s hash plus the canonical JSON of the event (chain, sequence, time, actor, impersonator, session, request, action, record, org, details). Sequences start at 1 with no gaps.',
          'An event is written in the **same database batch** as the change it records, so a change without its audit event cannot happen. Database triggers forbid updating or deleting audit events.',
        ],
      },
      {
        type: 'p',
        text: '**What is logged:** sign-ins and refusals, sign-outs, MFA changes; admin actions (orgs, users, staff, operators, invitations, subscriptions, entitlements, restrictions, impersonation); every record create, edit, submit, approve, publish, return, retract, release and correction; signal triage; manual feed runs and alert acknowledgements; exports; investor questions, participation requests and client actions; access denials (401, 403 and 404 for a signed-in caller, at most one per caller and route per minute); and staff cross-client reads (the inbox and the other cross-org staff views). Reads of records and minutes within a caller’s own scope are not logged. Scheduled feed runs and cron jobs are recorded in their own run tables instead.',
      },
      {
        type: 'list',
        items: [
          '**Verify** any chain from [Audit](/admin/audit): it recomputes the whole chain and reports the first broken link (sequence gap, previous-hash mismatch or hash mismatch).',
          '**Nightly anchor:** at 02:30 UTC every chain is verified and the head (sequence and hash) of each is written to R2 as `audit-anchors/YYYY-MM-DD.json`, even if a chain fails; a failure raises a critical alert. The anchors let anyone prove later that history was not rewritten.',
        ],
      },
      {
        type: 'callout',
        mode: 'demo',
        text: 'The seed writes a full audit history for the fictional world, so the chains verify from the start. A re-seed never deletes a chain that holds real events (such as admin sign-ins): it appends a `seed.loaded` event to it instead.',
      },
      {
        type: 'callout',
        mode: 'production',
        text: 'Same design, one chain per database (P-AUD-001): `platform` in the control plane, `core` in the Fruuit core database, and one chain in each tenant database for that investor’s activity, applied projections and exports. Heads are still anchored to R2 nightly (P-AUD-003).',
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'backups',
    title: 'Backups & recovery',
    summary: 'Nightly exports to R2, D1 Time Travel, and the production restore drill.',
    icon: DatabaseBackup,
    keywords: ['backup', 'restore', 'time travel', 'pitr', 'neon', 'r2', 'retention'],
    blocks: [
      {
        type: 'list',
        items: [
          '**Nightly export** (`d1-backup`, 02:30 UTC): every table is written to R2 as NDJSON under `backups/YYYY-MM-DD/<table>.ndjson`, with a `manifest.json` listing row counts and SHA-256 hashes.',
          '**Retention: 35 days**, enforced by the same job, which deletes older dates.',
          '**D1 Time Travel** can restore the database to any minute in its retention window (30 days on the Workers Paid plan, 7 on Free) with `wrangler d1 time-travel restore fruuit-engage --timestamp=<ISO time>`; note the bookmark it prints, which undoes the restore. It is the fastest recovery route and restores the whole database.',
          '**From the nightly export:** `packages/seed/scripts/restore.ts` downloads one night from R2 (`download --source=remote --day=YYYY-MM-DD`), checks every file’s SHA-256 against the manifest, restores it into a **fresh local** D1 (`restore --from=<dir>`) and verifies row counts, every audit chain and the night’s audit anchor. `--emit-sql=<dir>` writes the SQL files to load into a new remote database by hand. It never writes to the production database.',
          '**Restore drill:** `pnpm restore:drill` runs the nightly jobs on a local server, restores that export into a fresh local D1 and verifies it. Performed on 2026-10-22: 54 tables and 8,860 rows restored with no count or hash mismatches; 6 audit chains (1,313 events) recomputed and matching the R2 anchor. The steps and the record are in `docs/RUNBOOK.md`.',
        ],
      },
      {
        type: 'callout',
        mode: 'demo',
        text: 'Recovery in the demo is D1 Time Travel; the nightly export is the off-platform copy and has a tested restore path. For fictional data, a re-seed is often simplest (see [Demo data](#demo-data)).',
      },
      {
        type: 'callout',
        mode: 'production',
        items: [
          'Neon point-in-time restore on every project (7-day window in production), D1 Time Travel for the control plane, and the nightly logical export to R2 with 35-day retention (P-OPS-004).',
          'A **restore drill** before acceptance: restore one tenant to a Neon branch, check the app against it, and record it as evidence (PRD goal G5).',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'demo-data',
    title: 'Demo data',
    summary: 'The fictional world, real staff accounts, date rebasing, re-seeding and Kestrelmoor.',
    icon: FlaskConical,
    keywords: [
      'seed',
      'fictional',
      'rebase',
      'kestrelmoor',
      'cerro quillay',
      'personas',
      'seed:remote',
    ],
    blocks: [
      {
        type: 'list',
        items: [
          '**Fictional world** (generated from a fixed seed): 4 investor orgs (Fjordvakt, Silverlöv, Havbro, Tähkä) with 8 portfolios and 269 holdings; 62 companies across 15 sectors with 91 securities; 120 world events; 25 engagements with 29 objectives and 84 milestones; 32 meetings, 12 sets of minutes; questions, participation requests, client actions; and the feed history with signals and alerts.',
          'Fictional identifiers never collide with real ones: ISINs start `XF`/`XG`, LEIs start `9845FRUU`, emails end `.example`, links go to `example.invalid`, authorities are marked “(fictional)”.',
          'Seeded persona accounts have no sign-in identity; staff use “View as” to see their view.',
        ],
      },
      {
        type: 'callout',
        mode: 'demo',
        title: 'Real people use this demo',
        text: 'The Fruuit team (David Frazer, Michelle McCulloch, Antonios Panagiotopoulos, Conrad Albrecht) and George sign in with their own, real accounts. Treat everything you type as visible to them, and **never enter real client, holding or company data**.',
      },
      { type: 'h', text: 'Dates and re-seeding' },
      {
        type: 'list',
        items: [
          'The world is written around 22 October 2026. `pnpm seed:remote -- --yes` **rebases** every seeded date by whole days to today (UTC); local seeding keeps the original dates unless `--rebase-to` is given. Dates written in prose (“the 4 November meeting”) are not shifted.',
          'The server runs on real time (`DEMO_CLOCK` is empty), so “upcoming” and “overdue” drift as days pass after a seed.',
          'Without `--yes`, `seed:remote` prints its plan and changes nothing. With it, it applies migrations, replaces the data, re-creates the append-only triggers, and verifies row counts and every audit chain it touched. Only George runs it.',
        ],
      },
      {
        type: 'table',
        caption: 'What a re-seed wipes and keeps',
        head: ['Wiped', 'Kept'],
        rows: [
          [
            'Every investor org and all of its data (members, invitations, subscriptions, entitlements, portfolios, holdings, questions, requests, client actions, saved views, exports)',
            'The Fruuit org',
          ],
          [
            'All Fruuit research and canonical content, and all feed data',
            'Real users, their sign-in identities, Fruuit memberships, invitations, saved views and preferences',
          ],
          ['Seeded persona users', 'Sessions'],
          [
            'Command receipts',
            'Real job runs and non-feed alerts; any audit chain holding real events (the seed appends `seed.loaded` to it)',
          ],
        ],
      },
      {
        type: 'p',
        text: 'Seeded ids are stable across re-seeds, so bookmarks keep working.',
      },
      { type: 'h', text: 'The flagship journey: Kestrelmoor' },
      {
        type: 'p',
        text: 'Kestrelmoor Minerals plc and its Cerro Quillay tailings facility carry the full story: seepage in June 2025, a sanction proceeding and fine, a corrected assessment (v3), a board commitment at the 24 March meeting with reviewed minutes, milestones at S2, an unreleased S3 draft that investors cannot see, an overdue remedy action, and upcoming meetings. The demo script walks it from the manager’s dashboard through review and release to “View as” an investor and the committee report.',
      },
      {
        type: 'callout',
        mode: 'production',
        text: 'No seed. Production starts empty: the Fruuit org and staff, then the first client onboarded through configuration (org, invitations, holdings import, subscriptions and entitlements), all through the admin area and audited.',
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'configuration',
    title: 'Configuration & deployment',
    summary: 'Environment variables, secrets, deploying, migrations, rollback and CI.',
    icon: Settings2,
    keywords: [
      'env',
      'vars',
      'secrets',
      'deploy',
      'migration',
      'rollback',
      'ci',
      'wrangler',
      'healthz',
      'version',
    ],
    blocks: [
      {
        type: 'kv',
        caption: 'Variables (apps/api/wrangler.jsonc)',
        head: ['Variable', 'Purpose'],
        rows: [
          [
            '`APP_ORIGIN`',
            'The portal’s own origin; OAuth redirects, invite links and CSRF use it',
          ],
          [
            '`MARKETING_ORIGIN`',
            'The marketing site’s origin; reported by Admin → System and this page, not used for requests',
          ],
          ['`EMAIL_FROM`', 'Sender for sign-in codes, invitations and ops alerts'],
          [
            '`OPS_ALERT_EMAIL`',
            'Recipient of ops alert emails: feed failures and late deliveries, job failures, audit-verification failures',
          ],
          ['`ADMIN_DOMAIN`', 'Email domain admitted as platform admin'],
          ['`ADMIN_EXTRA_EMAILS`', 'Extra addresses admitted as platform admin'],
          ['`OPERATOR_EMAILS`', 'Addresses admitted as platform operator'],
          ['`FRUUIT_ENTRA_TID`', 'Fruuit’s Entra tenant; required for Microsoft admin sign-ins'],
          ['`PRIVILEGED_MFA`', '`required` or `optional`: second factor for privileged actions'],
          [
            '`DEMO_CLOCK`',
            'An ISO instant that pins the server clock (tests and demos); empty = real time',
          ],
          [
            '`EMAIL_DEV_LOG`',
            'Local dev only: log codes and invite links instead of emailing them',
          ],
        ],
      },
      {
        type: 'p',
        text: '**Secrets** (names only, set with `wrangler secret put`): `SESSION_SECRET`, `TOTP_ENC_KEY`, `GOOGLE_CLIENT_ID`, `GOOGLE_CLIENT_SECRET`, `MS_CLIENT_ID`, `MS_CLIENT_SECRET`, `RESEND_API_KEY`. Values live only as Worker secrets and in George’s local, git-ignored secrets file.',
      },
      {
        type: 'list',
        items: [
          '**Fails closed:** if `SESSION_SECRET` (16+ characters), `PRIVILEGED_MFA`, `DEMO_CLOCK`, `ADMIN_DOMAIN`, `FRUUIT_ENTRA_TID` or `APP_ORIGIN` is missing or invalid, every API request answers 503 and the names are only logged.',
          'A missing OAuth pair hides that sign-in button; a missing `RESEND_API_KEY` turns off email codes and emails; a missing `TOTP_ENC_KEY` turns off MFA enrolment.',
        ],
      },
      { type: 'live', view: 'config' },
      { type: 'h', text: 'Deploying' },
      {
        type: 'list',
        ordered: true,
        items: [
          '`pnpm deploy` first runs `pnpm verify`: lint, typecheck, every test suite, and the build.',
          'It applies pending D1 migrations to the remote database (`wrangler d1 migrations apply fruuit-engage --remote`). Migrations are forward-only and additive.',
          'It deploys the portal Worker, then the marketing Worker.',
          'It smoke-tests `/healthz` (`{"ok":true,…}`), `/signin` and the marketing home page.',
        ],
      },
      {
        type: 'list',
        items: [
          'Options: `--only=portal|marketing`, `--skip-verify`, `--dry-run`. Seeding is never part of a deploy.',
          '**CI** (GitHub Actions) runs `pnpm verify` and a production dependency audit on every push and pull request. Deploying from CI is a manual workflow run.',
          '**Rollback:** in `apps/api` (or `apps/marketing`), `wrangler deployments list` shows recent versions and `wrangler rollback [version-id] -m "reason"` switches back instantly, keeping secrets and bindings. Migrations are not rolled back, which is why they are additive; for data, use D1 Time Travel. Then check `/healthz` and sign-in.',
          'Full steps, the secrets inventory and rotation (including the Microsoft client secret, which expires **2027-10-22**), OAuth setup and DNS cautions are in `docs/DEPLOYMENT.md`.',
          '`/healthz` reports the version and whether D1 answers.',
        ],
      },
      {
        type: 'callout',
        mode: 'demo',
        text: 'One environment, deployed by hand from George’s machine or a manual CI run, into the Consultant-controlled account.',
      },
      {
        type: 'callout',
        mode: 'production',
        items: [
          '**Staging and production** (P-OPS-001/002): merging to `main` deploys to staging; a **tagged release** deploys to production, with a documented rollback.',
          'Separate secrets, OAuth apps and alert recipients per environment; secrets inventory (names only) in the handover pack.',
          'At the Handover Date: repository, build/deploy/rollback runbook, schema, API reference, secrets inventory, licences and operations runbook transfer to Fruuit (agreement §7A).',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'limitations',
    title: 'Known limitations & roadmap',
    summary: 'An honest list of what this build does not do yet.',
    icon: MapIcon,
    keywords: ['roadmap', 'limitations', 'todo', 'gaps', 'change order'],
    blocks: [
      { type: 'h', text: 'By design in the demo' },
      {
        type: 'list',
        items: [
          'One D1 database with logical isolation; no Neon, Hyperdrive, per-tenant databases, Queues, publication fan-out or staging.',
          'All feeds are demo feeds; the live provider and its licence wait for Provider Access (the Feed Milestone).',
          'MFA optional (`PRIVILEGED_MFA=optional`).',
          'No email or calendar invitations to companies or investors.',
          'CSV and print-to-PDF only: no XLSX, no branded PDF.',
        ],
      },
      { type: 'h', text: 'Open items before real client data' },
      {
        type: 'list',
        items: [
          'Require MFA; verify the Microsoft publisher; rotate the Microsoft client secret before 2027-10-22.',
          'Take manager and reviewer away from operators, or change admission, so operators read client data only through impersonation (P-ID-016).',
          'Decide EU-only storage (PRD Q11) and recreate R2 and KV in the `eu` jurisdiction if required.',
          'Delete stored exports after 90 days; repeat the restore drill against a production export in the target environment.',
        ],
      },
      { type: 'h', text: 'Known gaps in behaviour' },
      {
        type: 'list',
        items: [
          'Engagements and objectives cannot be retracted once released; revoke the subscription or archive instead.',
          'Only platform admins can restrict an engagement per user; the PRD gives this to investor admins.',
          'Which companies count as “authorized” differs slightly between company pages, search, reports and client actions.',
          'A meeting’s commitments are inferred from dates and stages (ADR-D04), not from an explicit link.',
          'Audit verification checks whole chains, not ranges.',
        ],
      },
      {
        type: 'callout',
        mode: 'production',
        text: 'Everything above is either covered by the production build (per-tenant data, staging, required MFA, the Feed Milestone) or listed as an open decision for David. XLSX, branded PDF and outbound notifications would be change orders.',
      },
    ],
  },
]

/** Section ids in order, for deep links (`/admin/docs#identity`). */
export const DOC_SECTION_IDS = DOC_SECTIONS.map((section) => section.id)
